import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import Spinner from "../Spinner";
import ImageThumbnail from "../ImageThumbnail";
import useRetryImage from '../ImageThumbnail/useRetryImage'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlay } from '@fortawesome/free-solid-svg-icons'
import { getItemWidth } from '../NubsUpload/NubsUpload.style'

const PreviewImage = styled.div`
  ${({ theme, error, width, hasVideo, fullWidth, container }) => {
    const itemWidth = getItemWidth(container?.clientWidth)

    return `
      background-color: ${theme.placeholderColor};
      display: flex;
      align-items: center;
      justify-content: center;
      width: ${width}px;
      height: 150px;
      position: relative;
      && {
        ${
            fullWidth
                ? `
                    margin: 0;
                    height: 100%;
                `
                : `
                    margin-right: 1rem;
                    margin-bottom: .5rem;
                `
          }
      }

      img {
        width: ${width}px;
        height: 75px;
        object-fit: contain;
      }

      ${
        hasVideo 
            ? `
                background: #000;
                width: 270px;
                height: 150px;

                ${theme.breakpoints.up('sm')} {
                    width: ${itemWidth};
                }
            ` : ''
      }

      ${error ? `
        border: 2px solid transparent;
    
        img, video {
          display: none;
        }
      ` : ''}
    `
  }}
`;

const ProcessingLabel = styled.span`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: 100%;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate3d(-50%,-50%,0);
    text-align: center;

    &.video-thumbnail__processing--failed {
        color: rgb(214, 39, 40);
    }

    > .svg-inline--fa {
        width: 18px;
        height: 18px;
    }
`;

const VideoThumbnail = (props) => {
    const { t } = useTranslation()
    const { 
        label, 
        width = 75, 
        isLoading, 
        isProcessing,
        errorState,
        loadedState,
        thumb,
        video, 
        fullWidth,
        className = '',
        container,
    } = props
    const {
        retryOnError,
        resetRetryOnLoad,
        retryKey,
        retryFailed,
    } = useRetryImage({ isVideo: true })
    const [error] = errorState
    const [loaded, setLoaded] = loadedState
    const mediaIsProcessing = useMemo(() => {
        // media is processing when it is being encoded in the background
        // and the poster image is not yet available
        return (isProcessing || error) && !isLoading
    }, [isProcessing, error, isLoading])
    const onError = useCallback(() => {
        setLoaded(false)
        retryOnError()
    }, [setLoaded, retryOnError])

    const onLoad = useCallback(() => {
        setLoaded(true)
        resetRetryOnLoad()
    }, [setLoaded, resetRetryOnLoad])
    return thumb || isLoading
        ? (
            <PreviewImage
                error={error}
                width={width}
                hasVideo={video && loaded}
                fullWidth={fullWidth}
                className={className}
                container={container}
            >
                {
                    retryFailed || mediaIsProcessing
                        ? (
                            <ProcessingLabel className={`video-thumbnail__processing ${retryFailed ? 'video-thumbnail__processing--failed' : ''}`}>
                                <FontAwesomeIcon icon={faPlay} />
                                {retryFailed ? t('Media failed to load') : t('Media is processing')}
                            </ProcessingLabel>
                        )
                        : null
                }
                {
                    isLoading
                        ? <Spinner />
                        : video
                            ? loaded
                                ? (
                                    <video
                                        controls
                                        width="270"
                                        height="100%"
                                        playsInline
                                        preload="metadata"
                                        poster={thumb}
                                    >
                                        <source src={video} type="video/mp4" preload="metadata" />
                                    </video>
                                )
                                : (
                                    <ImageThumbnail 
                                        key={retryKey}
                                        src={thumb} 
                                        alt={label} 
                                        onError={onError} 
                                        onLoad={onLoad} 
                                    />
                                )
                            : null
                }
            </PreviewImage>
        )
        : null
};

export default VideoThumbnail;
